/**
 * Order Log — mobile card density (ORD-1.6)
 * ─────────────────────────────────────────────────────────────────────────────
 * Three densities for the mobile card list, chosen from a "Card view" bottom
 * sheet and remembered per log in local storage. The mobile choice lives under
 * its own key and never touches the desktop column selection — the two
 * preferences are independent in both directions.
 */
import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { T } from '../../reportTokens';
import type { OrderLogRow, OrderLogStatus } from './orderLogModel';
import { REFUND_TYPE_LABEL } from '../../refunds/refundModel';

export type OrderLogCardDensity = 'compact' | 'standard' | 'financial';

const DENSITIES: { key: OrderLogCardDensity; label: string; hint: string }[] = [
    { key: 'compact', label: 'Compact', hint: 'Order, time, and total' },
    { key: 'standard', label: 'Standard', hint: 'Customer, table, staff, and items' },
    { key: 'financial', label: 'Financial', hint: 'Item total, discount, tax, and total' },
];

const STORAGE_PREFIX = 'reports.orderLog.cardView.';
const isDensity = (v: any): v is OrderLogCardDensity => v === 'compact' || v === 'standard' || v === 'financial';

/**
 * `scope` keys the preference per log ('paid', 'unpaid', 'sales'), so a view
 * chosen on one log leaves every other log alone. Storage failures (private
 * mode, a browser blocking site data) fall back to Standard rather than throw.
 */
export function useOrderLogCardDensity(scope: string): [OrderLogCardDensity, (next: OrderLogCardDensity) => void] {
    const key = STORAGE_PREFIX + scope;
    const [density, setDensity] = useState<OrderLogCardDensity>(() => {
        try {
            const stored = window.localStorage.getItem(key);
            return isDensity(stored) ? stored : 'standard';
        } catch {
            return 'standard';
        }
    });
    const choose = useCallback((next: OrderLogCardDensity) => {
        setDensity(next);
        try { window.localStorage.setItem(key, next); } catch { /* preference holds for this session only */ }
    }, [key]);
    return [density, choose];
}

// ─── header icon + "Card view" bottom sheet ──────────────────────────────────

export function OrderLogDensityButton({ value, onChange }: { value: OrderLogCardDensity; onChange: (next: OrderLogCardDensity) => void }) {
    const [open, setOpen] = useState(false);

    // Choosing a view only swaps the card template. Status, sort, refine
    // filters, search text and scroll position belong to the list and are
    // never touched here.
    const pick = (next: OrderLogCardDensity) => { onChange(next); setOpen(false); };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                aria-label="Card view"
                style={{ width: 28, height: 28, border: `1px solid ${T.cardBorder}`, borderRadius: 6, background: T.surface, color: T.t2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }}
            >
                <span className="material-symbols-rounded" style={{ fontSize: 15 }}>view_agenda</span>
            </button>
            {open && createPortal(
                // Above BottomNav (z-index 1000) so the sheet is never clipped by it.
                <div
                    onClick={() => setOpen(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(15,17,26,0.45)', display: 'flex', alignItems: 'flex-end' }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{ width: '100%', background: T.surface, borderTopLeftRadius: 16, borderTopRightRadius: 16, paddingBottom: 'max(12px, env(safe-area-inset-bottom))', fontFamily: T.font }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: `1px solid ${T.divider}` }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>Card view</span>
                            <button
                                onClick={() => setOpen(false)}
                                aria-label="Close"
                                style={{ width: 30, height: 30, borderRadius: 8, border: 'none', background: T.hover, color: T.t2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <span className="material-symbols-rounded" style={{ fontSize: 18 }}>close</span>
                            </button>
                        </div>
                        {DENSITIES.map(d => {
                            const active = d.key === value;
                            return (
                                <div
                                    key={d.key}
                                    onClick={() => pick(d.key)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', cursor: 'pointer', background: active ? T.accentSoft : 'transparent', borderBottom: `1px solid ${T.divider}` }}
                                >
                                    <span style={{ width: 17, height: 17, borderRadius: 9, border: `2px solid ${active ? T.accent : T.t3}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        {active && <span style={{ width: 8, height: 8, borderRadius: 4, background: T.accent }} />}
                                    </span>
                                    <span>
                                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: active ? T.accent : T.t1 }}>{d.label}</span>
                                        <span style={{ display: 'block', fontSize: 11, color: T.t3, marginTop: 1 }}>{d.hint}</span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
}

// ─── the card ────────────────────────────────────────────────────────────────

const EM_DASH = '—';
const dotted = (parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' · ');

/** Mobile cards always mask the phone — there is no reveal toggle here (CANC-1). */
const maskPhone = (raw: string | null): string | null => {
    if (!raw) return null;
    const digits = String(raw).replace(/\D/g, '');
    if (digits.length < 4) return null;
    return `(XXX) XXX-${digits.slice(-4)}`;
};

export interface OrderLogCardProps {
    row: OrderLogRow;
    density: OrderLogCardDensity;
    status?: OrderLogStatus;
    fmtMoney: (v: number) => string;
    /** Opens the order detail slide-over; omit to make the card inert */
    onOpen?: (row: OrderLogRow) => void;
    last?: boolean;
}

export function OrderLogCard({ row, density, status = 'paid', fmtMoney, onOpen, last = false }: OrderLogCardProps) {
    const cancelled = status === 'cancelled';
    const f = row.financials;
    const grand = f.grandTotal;
    const guestLabel = row.guests ? `${row.guests} guest${row.guests === 1 ? '' : 's'}` : null;

    // Meta lines stay single-line with an ellipsis — the same fields the desktop
    // table gives a column each. The line height keeps them legible stacked on a
    // phone instead of reading as one grey block.
    const line = (text: string, key: string) => (
        <div key={key} style={{ fontSize: 11, lineHeight: 1.45, color: T.t3, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</div>
    );

    const breakdownRow = (label: string, value: number | null, opts?: { total?: boolean; reduction?: boolean }) => {
        const missing = value === null || value === undefined;
        const valueColor = missing
            ? T.inkLight
            : opts?.total ? (cancelled ? T.inkMid : T.inkDark)
            : opts?.reduction && value > 0 ? T.inkMid
            : T.inkDark;
        return (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0' }}>
                <span style={{ fontSize: 12, color: opts?.total ? T.t1 : T.t2, fontWeight: opts?.total ? 600 : 400 }}>{label}</span>
                <span style={{ fontSize: 12, fontWeight: opts?.total ? 600 : 400, fontVariantNumeric: 'tabular-nums', color: valueColor }}>
                    {missing ? EM_DASH : `${opts?.reduction && value > 0 ? '−' : ''}${fmtMoney(value)}`}
                </span>
            </div>
        );
    };

    // The Financial view is a real card — its own border, corner radius and gap
    // between orders. It carries internal rules of its own (the breakdown and the
    // Grand Total), and against a plain divider list those read exactly like the
    // boundary between two orders, so one order's figures appear to run into the
    // next one's. Compact and Standard have no internal rules and stay a flat
    // divider list: the mobile read of the desktop table.
    const isFinancial = density === 'financial';
    const shell: React.CSSProperties = isFinancial
        ? {
            padding: '12px 14px',
            marginBottom: last ? 0 : 10,
            border: `1px solid ${T.cardBorder}`,
            borderRadius: 10,
            background: T.surface,
            boxShadow: T.shadow,
        }
        : { padding: '12px 0', borderBottom: last ? 'none' : `1px solid ${T.divider}` };

    return (
        <div
            onClick={() => onOpen && onOpen(row)}
            style={{ ...shell, cursor: onOpen ? 'pointer' : 'default' }}
        >
            {/* Order ID + Grand Total — present at every density */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.accent }}>#{row.orderNo}</span>
                <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: cancelled ? T.inkMid : T.inkDark }}>
                    {grand === null || grand === undefined ? EM_DASH : fmtMoney(grand)}
                </span>
            </div>

            {/* REF-5 — refund visibility at every density, not just the Financial one, and for
                cancelled orders too (a cancelled order can still carry a refund — matches the
                desktop table's order-id `undo` icon, which is gated only on row.hasRefund). */}
            {row.refundType && row.refunded != null && row.refunded > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                    <span className="material-symbols-rounded" style={{ fontSize: 12, color: T.orange, lineHeight: 1 }}>undo</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: T.red }}>
                        {REFUND_TYPE_LABEL[row.refundType]} refund · {fmtMoney(row.refunded)}
                    </span>
                </div>
            )}

            {density === 'compact' && line(dotted([row.orderDate, row.orderTime]) || EM_DASH, 'meta')}

            {density === 'standard' && (
                <>
                    {line(dotted([row.orderDate, row.orderTime, row.orderType, row.tableName]) || EM_DASH, 'meta')}
                    {line(dotted([row.customerName ?? 'Walk-in', maskPhone(row.customerPhone), guestLabel, row.staffName]), 'who')}
                    {/* Cancelled cards swap the items preview for the reason */}
                    {(cancelled ? row.cancelReason : row.itemPreview) && line((cancelled ? row.cancelReason : row.itemPreview) as string, 'items')}
                </>
            )}

            {density === 'financial' && (
                <>
                    {line(dotted([row.orderType, row.tableName, guestLabel]) || EM_DASH, 'meta')}
                    <div style={{ marginTop: 8, borderTop: `1px solid ${T.divider}`, paddingTop: 4 }}>
                        {breakdownRow('Item Total', f.itemTotal)}
                        {/* Discount earns a line only when the order actually carries one */}
                        {f.discount !== null && f.discount !== undefined && f.discount !== 0 && breakdownRow('Discount', f.discount, { reduction: true })}
                        {breakdownRow('Tax', f.tax)}
                        <div style={{ borderTop: `1px solid ${T.divider}`, marginTop: 2 }}>
                            {breakdownRow('Grand Total', grand, { total: true })}
                        </div>
                    </div>
                    {/* Always offered on an openable card. The slide-over prints every
                        component for every order — zeros included, an em dash where a
                        value is unavailable — so it always holds more than the four
                        lines above, plus items, customer, staff and refund history. */}
                    {onOpen && (
                        <button
                            onClick={e => { e.stopPropagation(); onOpen(row); }}
                            style={{ background: 'none', border: 'none', padding: '6px 0 0', color: T.accent, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: T.font }}
                        >
                            Full breakdown →
                        </button>
                    )}
                </>
            )}
        </div>
    );
}
